/**
 * Multi-Persona Selector Component
 *
 * Checkbox list for selecting multiple eval personas for the improvement loop.
 * Grouped by System (CEFR) and Custom, with select-all convenience.
 *
 * Story: 24.4 - Multi-Persona Content Improvement Engine
 */

import { useEvalPersonas, type EvalPersona } from '../services/personaApi';

interface MultiPersonaSelectorProps {
  selectedPersonaIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

export function MultiPersonaSelector({
  selectedPersonaIds,
  onSelectionChange,
}: MultiPersonaSelectorProps) {
  const { data: personas, isLoading } = useEvalPersonas();

  const systemPersonas = personas?.filter((p) => p.isSystem) || [];
  const customPersonas = personas?.filter((p) => !p.isSystem) || [];

  const togglePersona = (id: number) => {
    if (selectedPersonaIds.includes(id)) {
      const next = selectedPersonaIds.filter((pid) => pid !== id);
      if (next.length >= 1) onSelectionChange(next);
    } else {
      onSelectionChange([...selectedPersonaIds, id]);
    }
  };

  const selectAllSystem = () => {
    const systemIds = systemPersonas.map((p) => p.id);
    const customSelected = selectedPersonaIds.filter(
      (id) => !systemPersonas.some((p) => p.id === id)
    );
    onSelectionChange([...new Set([...customSelected, ...systemIds])]);
  };

  const deselectAll = () => {
    // Keep at least one selected
    if (selectedPersonaIds.length > 0) {
      onSelectionChange([selectedPersonaIds[0]]);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading personas...</p>;
  }

  if (!personas || personas.length === 0) {
    return <p className="text-sm text-gray-500">No eval personas found.</p>;
  }

  const allSystemSelected = systemPersonas.every((p) => selectedPersonaIds.includes(p.id));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">
          {selectedPersonaIds.length} persona{selectedPersonaIds.length !== 1 ? 's' : ''} selected
        </span>
        <div className="flex gap-2">
          {!allSystemSelected && (
            <button onClick={selectAllSystem} className="text-xs text-blue-600 hover:text-blue-800">
              Select All System
            </button>
          )}
          {selectedPersonaIds.length > 1 && (
            <button onClick={deselectAll} className="text-xs text-gray-500 hover:text-gray-700">
              Deselect Others
            </button>
          )}
        </div>
      </div>

      {systemPersonas.length > 0 && (
        <PersonaGroup
          label="System (CEFR)"
          personas={systemPersonas}
          selectedIds={selectedPersonaIds}
          onToggle={togglePersona}
        />
      )}

      {customPersonas.length > 0 && (
        <PersonaGroup
          label="Custom"
          personas={customPersonas}
          selectedIds={selectedPersonaIds}
          onToggle={togglePersona}
        />
      )}
    </div>
  );
}

function PersonaGroup({
  label,
  personas,
  selectedIds,
  onToggle,
}: {
  label: string;
  personas: EvalPersona[];
  selectedIds: number[];
  onToggle: (id: number) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <div className="space-y-1">
        {personas.map((p) => (
          <label
            key={p.id}
            className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(p.id)}
              onChange={() => onToggle(p.id)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="min-w-0">
              <span className="text-sm font-medium text-gray-800">{p.name}</span>
              <p className="text-xs text-gray-500 truncate">{p.description}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}
